import { BanknotesIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { Link } from 'libs/router'
import { routes } from 'config/routes'
import { PayoutStatus } from 'features/payouts/molecules/PayoutStatus/PayoutStatus'
import type { Booking } from 'features/bookings/types/bookings.types'
import { cn } from 'utils/style'

type CompletionPayoutProps = {
	booking: Booking
}

export const CompletionPayout = ({ booking }: CompletionPayoutProps) => {
	const { t } = useTranslation()
	const result = booking.completion_payout_result
	const payout = booking.completion_payout

	if (!result) return null

	const isWarning = result.code === 'not_positive' || result.code === 'error'

	const renderContent = () => {
		if (result.code === 'created' && payout) {
			return (
				<div className="flex flex-wrap items-center gap-2">
					<Link
						className="font-semibold text-primary hover:underline"
						to={routes.payoutEdit(payout.id)}>
						{t('Payout #{{ id }}', { id: payout.ref_nr })}
					</Link>
					<PayoutStatus status={payout.status} />
				</div>
			)
		}

		if (result.code === 'nothing_to_pay') {
			return <span>{t('No payout needed')}</span>
		}

		if (isWarning) {
			return (
				<>
					<span className="block">
						{t('Payout could not be created: {{ detail }}', { detail: result.detail })}
					</span>
					<Link
						className="mt-1 inline-block font-semibold underline"
						to={routes.userEdit(String(booking.user.pk))}>
						{t('Prepare the payout on the user')}
					</Link>
				</>
			)
		}

		return <span>{result.detail}</span>
	}

	return (
		<section className="rounded-lg border border-border bg-card p-5 shadow-panel">
			<h2 className="font-display text-base font-semibold text-foreground">
				{t('Payout at completion')}
			</h2>
			<p className="text-xs text-muted-foreground">
				{t('Prepared automatically when the booking was completed.')}
			</p>
			<div
				className={cn(
					'mt-4 flex items-start gap-3 rounded-md border px-3 py-2.5 text-[13px]',
					isWarning
						? 'border-warning/40 bg-warning-soft text-warning'
						: 'border-border bg-muted/50 text-foreground'
				)}>
				<BanknotesIcon className="mt-0.5 h-4 w-4 shrink-0 stroke-2" />
				<div className="min-w-0 flex-1">{renderContent()}</div>
			</div>
		</section>
	)
}
